// WU-2.2 — set/edit/clear a standing instruction. Two entry modes:
//   fixedScope   — dashboard lane affordance (scope shown, not editable)
//   free scope   — admin page (step select + job-type select, "" = step-wide)
// PUT is a replace: saving over an existing instruction supersedes it.
import { useState } from "react";
import { showApiError } from "@/lib/error-toast";
import { STEP_OPTIONS } from "@/lib/step-options";
import { queueInstructionsApi } from "../api/client";
import styles from "../queue-instructions.module.css";

const BODY_MAX = 500;

export interface InstructionDialogProps {
  /** Fixed lane scope (dashboard entry). Omit for free-scope (admin page). */
  fixedScope?: { stepType: string; jobTypeId: string | null; scopeLabel: string };
  /** Job types for the free-scope selector (admin page). Unused with fixedScope. */
  jobTypes?: { id: string; name: string }[];
  /** Pre-filled body when editing; null when creating. */
  initialBody: string | null;
  /** Existing active instruction id (enables the Clear button), or null. */
  existingId: string | null;
  onClose: () => void;
  /** Called after a successful PUT or DELETE so the caller refetches. */
  onSaved: () => void;
}

export function InstructionDialog({
  fixedScope,
  jobTypes = [],
  initialBody,
  existingId,
  onClose,
  onSaved,
}: InstructionDialogProps) {
  const [stepType, setStepType] = useState<string>(fixedScope?.stepType ?? "key");
  const [jobTypeId, setJobTypeId] = useState<string>(
    fixedScope ? (fixedScope.jobTypeId ?? "") : "",
  );
  const [body, setBody] = useState(initialBody ?? "");
  const [busy, setBusy] = useState(false);

  const trimmed = body.trim();
  const canSave = trimmed.length > 0 && !busy;

  // Save and clear share the same busy/error/onSaved+onClose shape — only the
  // API call differs.
  async function runMutation(mutate: () => Promise<unknown>) {
    setBusy(true);
    try {
      await mutate();
      onSaved();
      onClose();
    } catch (e) {
      showApiError(e);
    } finally {
      setBusy(false);
    }
  }

  function handleSave() {
    return runMutation(() =>
      queueInstructionsApi.put({
        stepType,
        ...(jobTypeId ? { jobTypeId } : {}),
        body: trimmed,
      }),
    );
  }

  function handleClear() {
    if (!existingId) return;
    return runMutation(() => queueInstructionsApi.delete(existingId));
  }

  return (
    <div className={styles.modalBackdrop} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <h2>{existingId ? "Edit instruction" : "Set instruction"}</h2>

        {fixedScope ? (
          <p className={styles.scopeLabel}>{fixedScope.scopeLabel}</p>
        ) : (
          <>
            <div className={styles.field}>
              <label htmlFor="qi-step"><span>Step</span></label>
              <select id="qi-step" value={stepType} aria-label="step"
                onChange={(e) => setStepType(e.target.value)}>
                {STEP_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label htmlFor="qi-jobtype"><span>Job type</span></label>
              <select id="qi-jobtype" value={jobTypeId} aria-label="job type"
                onChange={(e) => setJobTypeId(e.target.value)}>
                <option value="">All job types (step-wide)</option>
                {jobTypes.map((jt) => (
                  <option key={jt.id} value={jt.id}>{jt.name}</option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className={styles.field}>
          <label htmlFor="qi-body"><span>Instruction</span></label>
          <textarea
            id="qi-body"
            value={body}
            maxLength={BODY_MAX}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Standing guidance operators see on this queue"
            aria-label="instruction"
            rows={3}
          />
          <small className={styles.help}>
            Shown on the queue card and in the station. {body.length} / {BODY_MAX}
          </small>
        </div>

        <div className={styles.actions}>
          {existingId && (
            <button type="button" className={styles.danger} disabled={busy} onClick={handleClear}>
              Clear
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="button" className={styles.primary} disabled={!canSave} onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
